<template>
  <section class="duallo-admin-table">
    <header class="duallo-table-header">
      <div>
        <div class="duallo-eyebrow">Organization</div>
        <h3 class="duallo-table-title">Tags</h3>
      </div>
      <div class="table-controls">
        <v-switch v-model="showInactive" label="Show disabled" color="primary" hide-details inset density="compact" />
        <DisplayOverlayWindowButton ref="newTagWindow" buttonText="Add Tag" iconName="mdi-plus" windowTitle="Add Tag">
          <AddTagComponent @onTagCreated="tagCreated" @onCloseWindow="closeNewTagWindow" />
        </DisplayOverlayWindowButton>
      </div>
    </header>

    <div class="duallo-table-card">
      <div v-if="isLoading" class="duallo-state-block">
        <div v-for="n in 3" :key="n" class="duallo-row-skeleton" />
      </div>

      <div v-else-if="filteredTags.length === 0" class="duallo-state-block empty-state">
        <v-icon size="40" color="#4a5a60">mdi-tag-multiple-outline</v-icon>
        <p class="empty-title">No tags yet</p>
        <p class="empty-subtitle">Create a tag to group markers, hotspots and inspection zones across your assets.</p>
      </div>

      <template v-else>
        <div v-if="!isCompact" class="duallo-table-wrap">
          <table class="duallo-data-table">
            <thead>
              <tr>
                <th class="col-name">Name</th>
                <th class="col-group">Group</th>
                <th class="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              <TagTableItem v-for="tag in filteredTags" :key="tag.id" :tag="tag"
                :compact="false" @onTagModified="tagModified" />
            </tbody>
          </table>
        </div>

        <div v-else class="duallo-card-list">
          <TagTableItem v-for="tag in filteredTags" :key="tag.id" :tag="tag"
            :compact="true" @onTagModified="tagModified" />
        </div>
      </template>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useDisplay } from 'vuetify';
import TagService from '@/services/TagService';
import { useOrganizationStore } from '@/store/organizationStore';
import DisplayOverlayWindowButton from '../buttons/DisplayOverlayWindowButton.vue';
import AddTagComponent from '../AddTagComponent.vue';
import TagTableItem from './table-items/TagTableItem.vue';

const { smAndDown } = useDisplay();
const isCompact = computed(() => smAndDown.value);

const tags = ref([]);
const organizationStore = useOrganizationStore();
const isLoading = ref(true);
const showInactive = ref(false);
const newTagWindow = ref(null);

const filteredTags = computed(() =>
  tags.value.filter(t => showInactive.value ? true : t.isActive)
);

function closeNewTagWindow() {
  newTagWindow.value?.closeWindow();
}

const tagCreated = async () => {
  await loadTags();
  closeNewTagWindow();
};

const tagModified = async () => {
  await loadTags();
};

const loadTags = async () => {
  isLoading.value = true;
  try {
    tags.value = await TagService.getTagsByOrganizationId(organizationStore.organizationId);
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadTags);
</script>

<style scoped>
.table-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.table-controls :deep(.v-label) {
  color: #cfd8dc;
  font-size: 0.85rem;
}

.empty-state {
  align-items: center;
  text-align: center;
  padding: 40px 16px;
  color: #8aa0aa;
}

.empty-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #cfd8dc;
  margin: 12px 0 4px;
}

.empty-subtitle {
  font-size: 0.88rem;
  margin: 0;
  max-width: 380px;
}

.col-name { min-width: 160px; }
.col-group { min-width: 120px; }
.col-actions { width: 120px; text-align: right; }
</style>
